import { activeDateTimeFormatter } from "./formatting";
import { useEffect } from "react";

import { localeForFormatting } from "./preparedLocales";
import { useWorkspaceCopy } from "./workspaceCatalog";

const SEMANTIC_TIMESTAMP_SELECTOR = "time[datetime], [data-kavoosh-timestamp]";
const RELATIVE_SOURCE = /^(?:just now|\d+m ago|\d+h ago|\d+d ago)$/;
const originalMode = new WeakMap<Element, "relative" | "absolute">();

function timestampValue(element: Element): Date | null {
  const source = element instanceof HTMLTimeElement
    ? element.dateTime
    : element.getAttribute("data-kavoosh-timestamp");
  if (!source) return null;
  const value = new Date(source);
  return Number.isNaN(value.getTime()) ? null : value;
}

function relativeValue(value: Date, languageTag: string): string {
  const deltaSeconds = Math.round((value.getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(languageTag, { numeric: "auto" });
  const abs = Math.abs(deltaSeconds);
  if (abs < 60) return formatter.format(deltaSeconds, "second");
  if (abs < 3600) return formatter.format(Math.round(deltaSeconds / 60), "minute");
  if (abs < 86400) return formatter.format(Math.round(deltaSeconds / 3600), "hour");
  return formatter.format(Math.round(deltaSeconds / 86400), "day");
}

function absoluteValue(value: Date, languageTag: string): string {
  return activeDateTimeFormatter(languageTag, { dateStyle: "medium", timeStyle: "short" }).format(value);
}

function localizeTimestamp(element: Element, languageTag: string): void {
  const value = timestampValue(element);
  if (!value) return;
  let mode = originalMode.get(element);
  if (!mode) {
    mode = element.hasAttribute("data-kavoosh-relative-time") || RELATIVE_SOURCE.test((element.textContent ?? "").trim())
      ? "relative"
      : "absolute";
    originalMode.set(element, mode);
  }
  const next = mode === "relative" ? relativeValue(value, languageTag) : absoluteValue(value, languageTag);
  if (element.textContent !== next) element.textContent = next;
}

export function applyAdminDateLocalization(root: ParentNode, languageTag: string, path: string): void {
  if (!path.startsWith("/admin")) return;
  const shell = root.querySelector(".admin-v12-shell");
  if (!shell) return;
  shell.querySelectorAll(SEMANTIC_TIMESTAMP_SELECTOR).forEach((element) => localizeTimestamp(element, languageTag));
}

export function AdminDateLocalizationRuntime({ path }: { path: string }) {
  const { locale } = useWorkspaceCopy();
  useEffect(() => {
    if (!path.startsWith("/admin")) return;
    const languageTag = localeForFormatting(locale);
    let scheduled = false;
    const apply = () => { scheduled = false; applyAdminDateLocalization(document, languageTag, path); };
    apply();
    window.addEventListener("kavoosh:formatting-change", apply);
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(apply);
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["datetime", "data-kavoosh-timestamp", "data-kavoosh-relative-time"] });
    return () => { observer.disconnect(); window.removeEventListener("kavoosh:formatting-change", apply); };
  }, [locale, path]);
  return null;
}
